import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AssetItemDto, AVMetadataInfoDto, SimpleFolderItemDto } from '@api/Api';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import {
  toggleShareAssetModal,
  toggleRenameEntityModal,
  toggleEntityDeletionModal,
  toggleMoveAssetToPrivateFolder,
  toggleManageAssetVersionsModal,
  toggleApprovalModal,
  toggleApprovalAssetModal,
  toggleCopyModal,
  toggleAddToExistingLinkModal
} from '@redux/actions/modalAction';
import { Button, Dropdown, Tooltip } from 'antd';
import { ReactComponent as ActionSvg } from '@assets/icons/action.svg';
import { ReactComponent as PrivateEye } from '@assets/icons/eye-private.svg';
import {
  moveAssetToFolder,
  restoreDeletedAssetAction,
  setFavoriteAssetAction
} from '@redux/actions/assetsAction';
import { assetGetSimpleFoldersList } from '@api/Asset';
import { MixpanelEventType, MixpanelService } from '@services/mixpanelService';
import { useAuth } from '@hooks/useAuth';
import { download } from '@helpers/download';
import { ActionToast } from '@components/Toasts';
import { toast } from 'react-toastify';
import { useOrganization } from '@components/OrganizationBoundary';
import { useCurrentWorkspace } from '@hooks/workspace';
import { queryClient } from 'src/query-client';
import type { ItemType } from 'antd/lib/menu/hooks/useItems';

import { ReactComponent as HeartSvg } from '@assets/icons/heart.svg';
import { ReactComponent as ApprovalRoutingSvg } from '@assets/icons/approval-routing.svg';
import { ReactComponent as DeleteSvg } from '@assets/icons/delete.svg';
import { ReactComponent as DownloadInvoiceSvg } from '@assets/icons/download-invoice.svg';
import { ReactComponent as LinkSvg } from '@assets/icons/dropdown/link.svg';
import { ReactComponent as LinkPlusSvg } from '@assets/icons/dropdown/link-plus.svg';
import { ReactComponent as RenameSvg } from '@assets/icons/dropdown/rename.svg';
import { ReactComponent as MoveSvg } from '@assets/icons/dropdown/move.svg';
import { ReactComponent as CopySvg } from '@assets/icons/dropdown/copy.svg';
import { ReactComponent as DuplicateSvg } from '@assets/icons/dropdown/duplicate.svg';
import { ReactComponent as VersionsSvg } from '@assets/icons/dropdown/versions.svg';
import { useCopyAssetsMutation } from '@hooks/queries/assets';

type AssetActionsProps = {
  asset: AssetItemDto;
  canShare?: boolean;
  page: string;
};

function AssetActions({ asset, canShare, page }: AssetActionsProps) {
  const dispatch = useDispatch();
  const { user } = useAuth(true, 'internal');
  const navigate = useNavigate();
  const [foldersList, setFoldersList] = useState<SimpleFolderItemDto[]>([]);
  const [isCardMenuVisible, setIsCardMenuVisible] = useState<boolean>(false);
  const { currentOrganization } = useOrganization();
  const [currentWorkspace] = useCurrentWorkspace(false);

  const canCreateAw = !!currentWorkspace?.permissions.createApprovalWorkflows;
  const awFeatureEnabled =
    !!asset.asset?.approvalWorkflow ||
    !!currentOrganization.entity?.features.approvalRouting;
  const canManageAW = !!asset.asset?.approvalWorkflow?.permissions.manage;

  useEffect(() => {
    if (!isCardMenuVisible) return;
    (async () => {
      const {
        data: { list }
      } = await assetGetSimpleFoldersList({
        campaignId: asset.campaignId,
        workspaceId: asset.workspaceId
      });
      setFoldersList(list);
    })();
  }, [isCardMenuVisible]);

  const { mutateAsync: copyAssets, isPending: isCopyingAssets } =
    useCopyAssetsMutation({});

  const callbackAwModal = () => {
    queryClient.invalidateQueries({ queryKey: ['assets', 'list'] });
  };

  const awLabel = useMemo(() => {
    if (!!asset.asset?.approvalWorkflow && canManageAW) {
      return 'Manage approval routing';
    }
    if (!asset.asset?.approvalWorkflow && canCreateAw) {
      return 'Create approval routing';
    }
    return 'View approval routing';
  }, [asset.asset?.approvalWorkflow, canManageAW, canCreateAw]);

  const menuItems: ItemType[] = [
    ...(asset.asset?.permissions.download &&
    asset.asset?.versions[0].metadata.type === 'video'
      ? [
          {
            key: 'download',
            icon: <DownloadInvoiceSvg className="b-opacity-icon" />,
            disabled: !foldersList.length,
            label: 'Download',
            children: [
              {
                key: 'original',
                label: (
                  <>
                    Original{' '}
                    {
                      asset.asset?.versions[0].metadata.info.avInfo?.video
                        ?.height
                    }
                    p
                  </>
                ),
                onClick: () => {
                  download(
                    asset.asset?.versions[0].sourceUrl,
                    asset.asset?.versions[0].name
                  );
                }
              },
              {
                type: 'divider' as const
              },
              ...Object.entries(
                asset.asset?.versions[0].metadata.videoProxies || {}
              )
                .reverse()
                .filter((el) => el[1].status !== 'skipped')
                .map((el) => {
                  const info = asset.asset?.versions[0].metadata
                    .info as AVMetadataInfoDto;
                  if (`${info.avInfo?.video?.height}` === el[0])
                    return undefined;

                  return {
                    key: el[0],
                    label: `${el[0]}p mp4`,
                    disabled: el[1].status !== 'ready',
                    onClick: () => {
                      download(el[1].url);
                    }
                  };
                })
                .filter(
                  (item): item is NonNullable<typeof item> => item != null
                )
            ]
          }
        ]
      : []),
    ...(asset.asset?.permissions.download &&
    asset.asset?.versions[0].metadata.type !== 'video'
      ? [
          {
            key: 'download',
            icon: <DownloadInvoiceSvg className="b-opacity-icon" />,
            disabled: !asset.asset?.permissions.download,
            label: 'Download',
            onClick: () => {
              if (!asset.asset) return;
              const { name, sourceUrl } = asset.asset.versions[0];
              download(sourceUrl, name);
              setIsCardMenuVisible(false);
            }
          }
        ]
      : []),
    ...(canShare
      ? [
          {
            key: 'share',
            label: 'New share link',
            icon: <LinkPlusSvg />,
            onClick: () => {
              dispatch(
                toggleShareAssetModal({
                  visible: true,
                  campaignId: asset.campaignId,
                  assets: [
                    { id: asset.id, versionId: asset.asset?.versions[0]?.id }
                  ],
                  defaultName: asset.asset?.versions[0]?.name || ''
                })
              );
              setIsCardMenuVisible(false);
            }
          },
          {
            key: 'add-to-existing-link',
            label: 'Add to existing link',
            icon: <LinkSvg />,
            onClick: () => {
              dispatch(
                toggleAddToExistingLinkModal({
                  visible: true,
                  campaignId: asset.campaignId,
                  assets: [
                    { id: asset.id, versionId: asset.asset?.versions[0]?.id }
                  ]
                })
              );
              setIsCardMenuVisible(false);
            }
          }
        ]
      : []),
    {
      type: 'divider'
    },
    ...(asset.asset?.permissions.rename
      ? [
          {
            key: 'rename',
            icon: <RenameSvg />,
            label: 'Rename',
            onClick: () => {
              dispatch(
                toggleRenameEntityModal({
                  visible: true,
                  entity: asset,
                  page: 'media'
                })
              );
              setIsCardMenuVisible(false);
            }
          }
        ]
      : []),
    {
      key: 'duplicate-folder',
      label: 'Duplicate',
      disabled: isCopyingAssets,
      icon: <DuplicateSvg />,
      onClick: () => {
        copyAssets({
          workspaceId: asset.workspaceId,
          assetIds: [asset.id],
          targetCampaignId: asset.campaignId,
          targetFolderId: asset.path?.[0]?.id,
          copyComments: true
        });
        setIsCardMenuVisible(false);
      }
    },
    {
      key: 'copy-to',
      label: 'Copy to',
      icon: <CopySvg />,
      onClick: () => {
        dispatch(
          toggleCopyModal({
            visible: true,
            type: 'assets',
            assets: [
              {
                id: asset.id,
                name: `${asset.asset?.versions[0]?.name}.${asset.asset?.versions[0]?.extension}`
              }
            ]
          })
        );
        setIsCardMenuVisible(false);
      }
    },
    ...(asset.asset?.permissions.moveToPrivateFolder ||
    asset.asset?.permissions.moveToPublicFolder
      ? [
          {
            key: 'move_to',
            label: 'Move to',
            icon: <MoveSvg />,
            popupClassName: 'asset_links_overlay',
            disabled: !foldersList.length,
            popupOffset: [4, -20],
            children: [
              ...(!!asset.asset?.permissions.moveToCampaign &&
              !!asset.path.length
                ? [
                    {
                      key: 'project_label',
                      label: 'PROJECT',
                      type: 'group' as const,
                      children: [
                        {
                          key: 'campaign',
                          label: asset.campaignName,
                          onClick: () => {
                            dispatch(
                              moveAssetToFolder(
                                {
                                  assetId: asset.id
                                },
                                page === 'campaign',
                                asset.path
                              )
                            );
                            setIsCardMenuVisible(false);
                          }
                        }
                      ]
                    }
                  ]
                : []),
              ...(foldersList.length
                ? [
                    {
                      key: 'folder_label',
                      label: 'FOLDER',
                      type: 'group' as const,
                      children: foldersList?.map((el) => {
                        if (asset.path.find((it) => it.id === el.id))
                          return null;
                        return {
                          key: el.id,
                          label: (
                            <>
                              {el.name.length > 10 ? (
                                <Tooltip
                                  placement="topLeft"
                                  overlayClassName="toggle_favorite_overlay toggle_favorite_overlay--small"
                                  title={el.name}
                                >
                                  <span>{el.name}</span>
                                </Tooltip>
                              ) : (
                                <span>{el.name}</span>
                              )}
                              {el.isPrivate && <PrivateEye />}
                            </>
                          ),
                          disabled: el.isPrivate
                            ? !asset.asset?.permissions.moveToPrivateFolder
                            : !asset.asset?.permissions.moveToPublicFolder,
                          onClick: () => {
                            if (el.isPrivate) {
                              dispatch(
                                toggleMoveAssetToPrivateFolder({
                                  folderName: el.name,
                                  visible: true,
                                  assetsName: [
                                    asset.asset?.versions[0].name || ''
                                  ],
                                  callback: () => {
                                    dispatch(
                                      moveAssetToFolder(
                                        {
                                          assetId: asset.id,
                                          folderId: el.id
                                        },
                                        page === 'campaign',
                                        asset.path
                                      )
                                    );
                                  }
                                })
                              );
                              setIsCardMenuVisible(false);
                              return;
                            }
                            dispatch(
                              moveAssetToFolder(
                                {
                                  assetId: asset.id,
                                  folderId: el.id
                                },
                                page === 'campaign',
                                asset.path
                              )
                            );
                            setIsCardMenuVisible(false);
                          }
                        };
                      })
                    }
                  ]
                : [])
            ]
          }
        ]
      : []),
    {
      type: 'divider'
    },
    ...(awFeatureEnabled
      ? [
          {
            key: 'manage-aw',
            icon: <ApprovalRoutingSvg className="b-opacity-icon" />,
            label: awLabel,
            onClick: () => {
              setIsCardMenuVisible(false);
              if (!asset.asset?.versions[0]) return;
              if (!currentWorkspace?.id) return;
              if (asset.asset?.approvalWorkflow) {
                dispatch(
                  toggleApprovalAssetModal({
                    assetId: asset.id,
                    visible: true,
                    versionId: asset.asset.versions[0].id,
                    approvalWorkflowId: asset.asset?.approvalWorkflow.id,
                    callback: callbackAwModal
                  })
                );
                return;
              }
              dispatch(
                toggleApprovalModal({
                  visible: true,
                  approvalWorkflowId: asset.asset?.approvalWorkflow?.id,
                  assetVersions: [
                    {
                      ...asset.asset?.versions[0],
                      assetId: asset.id
                    }
                  ],
                  permissions: {
                    create: canCreateAw
                  },
                  campaignId: asset.campaignId,
                  callback: (aw) => {
                    if (aw) {
                      navigate(
                        `/campaigns/approvals/${aw.workspaceId}/${aw.campaignId}/${aw.id}`
                      );
                      return;
                    }
                    callbackAwModal();
                  }
                })
              );
            }
          }
        ]
      : []),
    {
      key: 'manage-version-stack',
      label: 'Manage version stack',
      icon: <VersionsSvg />,
      onClick: () => {
        dispatch(
          toggleManageAssetVersionsModal({
            versions: asset?.asset?.versions || [],
            permissions: asset?.asset?.permissions || null,
            versionsCount: asset?.asset?.versionsCount || 0,
            visible: true,
            assetId: asset.id || ''
          })
        );
        setIsCardMenuVisible(false);
      }
    },
    {
      key: 'add_to_favorites',
      label: asset?.isFavorite ? 'Remove from favorites' : 'Add to favorites',
      icon: (
        <HeartSvg className={classNames({ 'b-favorite': asset.isFavorite })} />
      ),
      onClick: () => {
        if (asset.deletedAt) return;
        if (!asset?.isFavorite) {
          dispatch(
            setFavoriteAssetAction(
              {
                assetId: asset.id,
                isFavorite: !asset.isFavorite
              },
              page === 'favorite'
            )
          );
          MixpanelService.track(user.id, MixpanelEventType.MANAGE_ASSETS, {
            addToFavorite: true,
            deleteAsset: false,
            newAsset: false,
            externalLinkShare: false,
            approvalRouting: false,
            newCommentAsset: false,
            annotationLeft: false,
            taskFromComment: false
          });
          setIsCardMenuVisible(false);
          return;
        }
        dispatch(
          setFavoriteAssetAction(
            {
              assetId: asset.id,
              isFavorite: !asset.isFavorite
            },
            page === 'favorite'
          )
        );
        toast(
          <ActionToast
            title="Media has been removed from favorites"
            onUndo={undefined}
            description={undefined}
            closeToast={undefined}
          />,
          {
            hideProgressBar: true,
            bodyClassName: 'toast_container--invitation'
          }
        );

        setIsCardMenuVisible(false);
      }
    },
    ...(asset.asset?.permissions.delete
      ? [
          {
            type: 'divider' as const
          },
          {
            key: 'delete',
            label: 'Delete',
            icon: <DeleteSvg className="b-opacity-icon" />,
            onClick: () => {
              dispatch(
                toggleEntityDeletionModal({
                  entity: asset,
                  page: 'asset',
                  permanent: false,
                  visible: true
                })
              );

              setIsCardMenuVisible(false);
            }
          }
        ]
      : [])
  ];

  const deleteMenuItems: ItemType[] = asset.asset?.permissions.delete
    ? [
        {
          key: 'delete_permanent',
          label: 'Delete forever',
          onClick: () => {
            dispatch(
              toggleEntityDeletionModal({
                entity: asset,
                page: 'asset',
                permanent: true,
                visible: true
              })
            );
            setIsCardMenuVisible(false);
          }
        },
        {
          key: 'restore',
          label: 'Restore',
          onClick: () => {
            dispatch(
              restoreDeletedAssetAction({
                assetId: asset.id
              })
            );
            setIsCardMenuVisible(false);
          }
        }
      ]
    : [];

  return (
    <Dropdown
      open={isCardMenuVisible}
      onOpenChange={(v) => setIsCardMenuVisible(v)}
      trigger={['click']}
      overlayClassName="asset_links_overlay"
      menu={{
        items: !asset.deletedAt ? menuItems : deleteMenuItems
      }}
    >
      <Button type="text" size="small" style={{ padding: 0 }}>
        <ActionSvg style={{ marginRight: 0 }} />
      </Button>
    </Dropdown>
  );
}

export default AssetActions;
